'use client'

import React from 'react'
// eslint-disable-next-line no-restricted-imports -- grandfathered; prefer next/image for new code
import { Button, Flex, Image, Text } from '@mantine/core'
import { useIsMobile } from '@/lib/mobileDetector'
import { appPath } from '@/lib/getPublicAppUrl'

export enum CTAType {
    CENTER = 'center',
    GAP = 'gap',
}

type CallToActionCardProps = {
    image?: any
    header?: string
    title?: string
    subtitle?: string
    actionLabel?: string
    actionIcon?: React.ReactNode
    actionHref?: string
    actionSubtitle?: string
    transparent?: boolean
    align?: 'left' | 'center' | 'right'
    onClick?: () => void
    ctaType?: CTAType
    secondaryActionLabel?: string
    secondaryActionIcon?: React.ReactNode
    secondaryActionHref?: string
    secondaryOnClick?: () => void
}

const CallToActionCard: React.FC<CallToActionCardProps> = ({
    image,
    header,
    title,
    subtitle,
    actionLabel,
    actionIcon,
    actionHref,
    actionSubtitle,
    transparent,
    align,
    onClick,
    ctaType = CTAType.CENTER,
    secondaryActionLabel,
    secondaryActionIcon,
    secondaryActionHref,
    secondaryOnClick,
}) => {
    const isMobile = useIsMobile()
    const isCenteredCTA = ctaType === CTAType.CENTER
    const hasSecondaryAction = !!secondaryActionLabel

    return (
        <Flex justify="center" mt="auto" w={'100%'}>
            <Flex
                direction={(isMobile && !hasSecondaryAction) || isCenteredCTA ? 'column' : 'row'}
                align={align ? align : 'flex-start'}
                ta={align ? align : 'center'}
                justify={isCenteredCTA ? 'center' : 'space-between'}
                bg={transparent ? '' : 'white'}
                p={24}
                w="100%"
                gap={isMobile || isCenteredCTA ? 12 : 24}
                style={{
                    ...(transparent
                        ? {}
                        : {
                              borderRadius: 16,
                              boxShadow: transparent ? '' : '0 2px 6px rgba(0, 0, 0, 0.1)',
                          }),
                }}
            >
                {isCenteredCTA ? renderCenteredCTA() : renderGapCTA()}
            </Flex>
        </Flex>
    )

    function renderCenteredCTA() {
        return (
            <>
                {image && renderImage()}
                {header && renderHeader()}
                {title && renderTitle()}
                {subtitle && renderSubtitle()}
                {renderButtons()}
                {actionSubtitle && renderActionSubtitle()}
            </>
        )
    }

    function renderGapCTA() {
        return (
            <>
                <Flex
                    direction="column"
                    align={isMobile ? 'center' : 'flex-start'}
                    ta={isMobile ? 'center' : 'left'}
                    gap={8}
                >
                    {image && renderImage()}
                    {header && renderHeader()}
                    {title && renderTitle()}
                    {subtitle && renderSubtitle()}
                    {actionSubtitle && renderActionSubtitle()}
                </Flex>

                <Flex direction={isMobile && !hasSecondaryAction ? 'column' : 'row'} gap={8} align="center">
                    {actionLabel && renderActionButton()}
                    {secondaryActionLabel && renderSecondaryActionButton()}
                </Flex>
            </>
        )
    }

    function renderImage() {
        const isImageFromText = typeof image === 'string'

        return <Image src={isImageFromText ? image : image.src} alt={isImageFromText ? image : image.alt} w={120} />
    }

    function renderHeader() {
        return (
            <Text fz={24} fw={450} c="var(--gm-foreground-fg-primary)">
                {header}{' '}
            </Text>
        )
    }

    function renderTitle() {
        return (
            <Text fz={22} fw={500} c="var(--gm-foreground-fg-primary)">
                {title}
            </Text>
        )
    }

    function renderSubtitle() {
        return (
            <Text fz={16} c={'var(--gm-foreground-fg-secondary, #4F4E4D)'}>
                {subtitle}
            </Text>
        )
    }

    function renderButtons() {
        return (
            <Flex
                direction={isMobile && !hasSecondaryAction ? 'column' : 'row'}
                gap={8}
                w="100%"
                justify="center"
                align="center"
            >
                {actionLabel && renderActionButton()}
                {secondaryActionLabel && renderSecondaryActionButton()}
            </Flex>
        )
    }

    function renderActionButton() {
        return (
            <Button
                h={isMobile && hasSecondaryAction ? 'var(--spacing-40, 40px)' : 'var(--spacing-52, 52px)'}
                component="a"
                size="xs"
                href={actionHref && appPath(actionHref)}
                leftSection={actionIcon}
                onClick={onClick}
                fullWidth={isMobile || !hasSecondaryAction}
                styles={{
                    inner: {
                        flexWrap: 'nowrap',
                        whiteSpace: 'normal',
                        overflow: 'visible',
                    },
                }}
            >
                {actionLabel}
            </Button>
        )
    }

    function renderSecondaryActionButton() {
        return (
            <Button
                h={isMobile && hasSecondaryAction ? 'var(--spacing-40, 40px)' : 'var(--spacing-52, 52px)'}
                component="a"
                size="sm"
                leftSection={secondaryActionIcon}
                onClick={secondaryOnClick}
                href={secondaryActionHref && appPath(secondaryActionHref)}
                fullWidth={isMobile}
                variant="outline"
                c="var(--gm-gray-9)"
                styles={{
                    root: {
                        border: '1px solid var(--gm-foreground-fg-outline-primary)',
                    },
                    inner: {
                        flexWrap: 'nowrap',
                        whiteSpace: 'normal',
                        overflow: 'visible',
                    },
                }}
            >
                {secondaryActionLabel}
            </Button>
        )
    }

    function renderActionSubtitle() {
        return (
            <Text fz={14} fw={500} c="gray.5" ta="center" w="100%" lh="140%">
                {actionSubtitle}
            </Text>
        )
    }
}

export default CallToActionCard
